/**
 * A small, tolerant XML reader for reference-manager exports.
 *
 * EndNote and similar tools write machine-generated XML, so this reads elements,
 * attributes, text, CDATA and the five predefined entities plus numeric character
 * references, and skips comments, processing instructions and the DOCTYPE. It is not a
 * validating parser: a mismatched closing tag closes the nearest matching ancestor, and
 * anything left open at the end is closed, with the problem recorded. It does not use
 * `DOMParser`, so it behaves the same in a browser and in a test.
 */

export interface XmlElement {
  name: string;
  attrs: Record<string, string>;
  children: XmlElement[];
  /** Text directly inside this element (not its children), joined in document order. */
  text: string;
}

export interface XmlParse {
  /** A synthetic root holding the document's top-level elements. */
  root: XmlElement;
  errors: string[];
}

const ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
};
const RE_ENTITY = /&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z][a-zA-Z0-9]*);/g;
const RE_ATTR = /([^\s=/>]+)\s*=\s*("([^"]*)"|'([^']*)')/g;
const RE_NAME = /^[^\s/>]+/;
const RE_SPACES = /\s+/g;

function codePoint(value: number): string {
  try {
    return String.fromCodePoint(value);
  } catch {
    return "";
  }
}

export function decodeEntities(value: string): string {
  if (!value.includes("&")) {
    return value;
  }
  return value.replace(RE_ENTITY, (match, body: string) => {
    if (body.startsWith("#x")) {
      return codePoint(Number.parseInt(body.slice(2), 16));
    }
    if (body.startsWith("#")) {
      return codePoint(Number.parseInt(body.slice(1), 10));
    }
    return ENTITIES[body] ?? match;
  });
}

function attributes(source: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  for (const match of source.matchAll(RE_ATTR)) {
    attrs[match[1]] = decodeEntities(match[3] ?? match[4] ?? "");
  }
  return attrs;
}

/** The element currently open. The stack always holds at least the root. */
function current(stack: XmlElement[]): XmlElement {
  return stack.at(-1) ?? stack[0];
}

function element(name: string, attrs: Record<string, string>): XmlElement {
  return { name, attrs, children: [], text: "" };
}

/** Index just past the end of the construct that opens at `at`, or -1. */
function skipPast(source: string, at: number, close: string): number {
  const end = source.indexOf(close, at);
  return end < 0 ? -1 : end + close.length;
}

/** The end of a tag starting at `at`, honouring quotes inside attribute values. */
function tagEnd(source: string, at: number): number {
  let quote = "";
  for (let index = at; index < source.length; index++) {
    const char = source[index];
    if (quote) {
      quote = char === quote ? "" : quote;
    } else if (char === '"' || char === "'") {
      quote = char;
    } else if (char === ">") {
      return index;
    }
  }
  return -1;
}

export function parseXml(input: string): XmlParse {
  const source = input.startsWith("﻿") ? input.slice(1) : input;
  const root = element("#root", {});
  const errors: string[] = [];
  const stack: XmlElement[] = [root];
  let at = 0;

  while (at < source.length) {
    const open = source.indexOf("<", at);
    if (open < 0) {
      current(stack).text += decodeEntities(source.slice(at));
      break;
    }
    if (open > at) {
      current(stack).text += decodeEntities(source.slice(at, open));
    }
    at = readMarkup(source, open, stack, errors);
    if (at < 0) {
      errors.push("The file ends in the middle of a tag.");
      break;
    }
  }
  if (stack.length > 1) {
    errors.push(`${stack.length - 1} element(s) were never closed.`);
  }
  return { root, errors };
}

/** Handle the markup at `open`; returns the index to continue from, or -1 at a truncation. */
function readMarkup(
  source: string,
  open: number,
  stack: XmlElement[],
  errors: string[]
): number {
  if (source.startsWith("<!--", open)) {
    return skipPast(source, open, "-->");
  }
  if (source.startsWith("<![CDATA[", open)) {
    const end = source.indexOf("]]>", open);
    if (end < 0) {
      return -1;
    }
    current(stack).text += source.slice(open + 9, end);
    return end + 3;
  }
  if (source.startsWith("<?", open)) {
    return skipPast(source, open, "?>");
  }
  if (source.startsWith("<!", open)) {
    const end = tagEnd(source, open);
    return end < 0 ? -1 : end + 1;
  }
  const end = tagEnd(source, open + 1);
  if (end < 0) {
    return -1;
  }
  const body = source.slice(open + 1, end);
  if (body.startsWith("/")) {
    closeElement(body.slice(1).trim(), stack, errors);
  } else {
    openElement(body, stack);
  }
  return end + 1;
}

function openElement(body: string, stack: XmlElement[]): void {
  const selfClosing = body.endsWith("/");
  const inner = selfClosing ? body.slice(0, -1) : body;
  const name = RE_NAME.exec(inner)?.[0] ?? "";
  const node = element(name, attributes(inner.slice(name.length)));
  current(stack).children.push(node);
  if (!selfClosing) {
    stack.push(node);
  }
}

function closeElement(name: string, stack: XmlElement[], errors: string[]) {
  for (let depth = stack.length - 1; depth > 0; depth--) {
    if (stack[depth].name === name) {
      stack.length = depth;
      return;
    }
  }
  errors.push(`A closing </${name}> tag has no matching opening tag.`);
}

/* ------------------------------------------------------------- queries */

/** All the text inside an element, children included, whitespace collapsed. */
export function textOf(node: XmlElement | undefined): string {
  if (!node) {
    return "";
  }
  const parts: string[] = [];
  collectText(node, parts);
  return parts.join("").replace(RE_SPACES, " ").trim();
}

function collectText(node: XmlElement, parts: string[]): void {
  parts.push(node.text);
  for (const child of node.children) {
    collectText(child, parts);
  }
}

/** Direct children called `name`. */
export function childrenNamed(node: XmlElement, name: string): XmlElement[] {
  return node.children.filter((child) => child.name === name);
}

export function firstChild(
  node: XmlElement | undefined,
  name: string
): XmlElement | undefined {
  return node?.children.find((child) => child.name === name);
}

/** Follow a path of child names, taking the first match at each step. */
export function descend(
  node: XmlElement | undefined,
  path: readonly string[]
): XmlElement | undefined {
  let current = node;
  for (const name of path) {
    current = firstChild(current, name);
  }
  return current;
}

/** Every descendant (depth-first) called `name`. */
export function findAll(node: XmlElement, name: string): XmlElement[] {
  const found: XmlElement[] = [];
  const visit = (current: XmlElement) => {
    for (const child of current.children) {
      if (child.name === name) {
        found.push(child);
      }
      visit(child);
    }
  };
  visit(node);
  return found;
}
